import axios from 'axios';
import { getAccessToken, getTenantId } from '../store/authStore';

// PAI Academy service. Same direct-per-service pattern as api/client.ts —
// the learner's session runs IN their Kitchen tenant, so the interceptor's
// X-Tenant-ID header is what scopes every call. Enrollment + verify are the
// two pre-auth surfaces (the login page calls enroll before any token exists).

const ACADEMY_BASE_URL =
  import.meta.env.VITE_ACADEMY_SERVICE_URL || 'http://localhost:8034';

const academyClient = axios.create({
  baseURL: ACADEMY_BASE_URL,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
});

academyClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const tenant = getTenantId();
  if (tenant) config.headers['X-Tenant-ID'] = tenant;
  return config;
});

export const ACADEMY_URL = ACADEMY_BASE_URL;

export interface AcademyStep {
  id: string;
  page?: string;
  target?: string;
  title: string;
  body: string;
  action?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  checks?: unknown[];
}

export interface LessonSection {
  heading: string;
  body: string;
}

export interface LessonQuizQuestion {
  q: string;
  options: string[];
}

export interface AcademyLesson {
  id: string;
  title: string;
  minutes: number;
  video_url: string | null;
  sections: LessonSection[];
  quiz: LessonQuizQuestion[];
}

export interface CheckFeedback {
  label: string;
  passed: boolean;
  feedback: string;
}

export interface ExamAttempt {
  id: string;
  attempt_no: number;
  started_at: string | null;
  deadline_at: string | null;
  submitted_at: string | null;
  score: number | null;
  passed: boolean | null;
  per_check_results:
    | { label: string; weight: number; passed: boolean; results: CheckFeedback[] }[]
    | null;
  /** Bake-Off incidents: whether each act has fired (never the answer key). */
  events?: { sabotage_fired: boolean; inject_fired: boolean };
}

export interface TrackSummary {
  key: string;
  version: string;
  title: string;
  description: string;
  lessons: number;
  steps: number;
  proofed_steps: number;
}

export interface BadgeInfo {
  verify_code: string;
  title: string;
  issued_at: string;
  /** LinkedIn "Add to profile" deep link (issuer maic, public verify URL). */
  linkedin_url?: string;
}

/** One question of the Easy as PAI results page. `correct` and `explanation`
 *  are null for a question you missed on a failed attempt: retries are
 *  unlimited, so the bank is only revealed once you pass. */
export interface BadgeReviewItem {
  q: string;
  options: string[];
  /** Hands-on course the question is about (QUIZ-3); null for conceptual ones. */
  course?: string | null;
  chosen: number;
  is_correct: boolean;
  correct: number | null;
  explanation: string | null;
}

export interface BadgeSubmitResult {
  passed: boolean;
  correct: number;
  total: number;
  pass_count: number;
  review: BadgeReviewItem[];
  badge: BadgeInfo | null;
  /** null: no badge issued by this submit; otherwise the email outcome. */
  email_sent: boolean | null;
}

export interface ProgramStats {
  learners: number;
  enrollments: number;
  kitchens: number;
  lessons_completed: number;
  steps_proofed: number;
  exams: {
    attempts: number;
    passed: number;
    pass_rate: number | null;
    avg_score: number | null;
  };
  certificates: { total: number; badges: number };
  tracks: {
    track_key: string;
    title: string;
    enrolled: number;
    lessons_done: number;
    recipe_done: number;
    certified: number;
  }[];
  recent_certificates: {
    holder: string;
    title: string;
    kind: string;
    verify_code: string;
    issued_at: string | null;
  }[];
}

export interface MyEnrollment {
  /** True when a maic user in their ORIGIN tenant is reading their linked
   *  Kitchen's state: read-only, the writes need the Kitchen session. */
  origin_view?: boolean;
  badge: BadgeInfo | null;
  whole_pai: BadgeInfo | null;
  my_tracks: {
    track_key: string;
    title: string;
    status: string;
    lessons_done: number;
    lessons_total: number;
    steps_done: number;
    steps_total: number;
  }[];
  available_tracks: {
    track_key: string;
    title: string;
    description: string;
    locked: boolean;
  }[];
  enrollment: {
    id: string;
    track_key: string;
    user_email: string;
    user_name: string;
    kitchen_tenant_id: string;
    status: string;
  };
  track: {
    key: string;
    title: string;
    lessons: AcademyLesson[];
    steps: AcademyStep[];
    exam: {
      duration_minutes: number;
      pass_score: number;
      brief: string;
      answer_fields: { key: string; label: string }[];
    };
  };
  step_status: Record<
    string,
    { status: string; attempts: number; proofed_at: string | null }
  >;
  exam_attempt: ExamAttempt | null;
  certificate: {
    id: string;
    verify_code: string;
    title: string;
    issued_at: string;
    /** The learner's own score. Absent from the PDF and the public verify page. */
    score: number | null;
    /** Prefilled LinkedIn "Add to profile" deep link, built server-side. */
    linkedin_url: string;
  } | null;
}

export async function listTracks(): Promise<TrackSummary[]> {
  const { data } = await academyClient.get('/academy/tracks');
  return data.tracks;
}

/** One certification's public syllabus: lessons (quiz without answers),
 *  recipe steps and the exam rules. Public, like the catalog (GSL-3). */
export interface TrackDetail {
  key: string;
  version: string;
  title: string;
  description: string;
  lessons: AcademyLesson[];
  steps: AcademyStep[];
  exam: {
    duration_minutes: number;
    pass_score: number;
    brief: string;
    answer_fields: { key: string; label: string }[];
  };
}

export async function fetchTrackDetail(key: string): Promise<TrackDetail> {
  const { data } = await academyClient.get(`/academy/tracks/${encodeURIComponent(key)}`);
  return data;
}

export async function enroll(payload: {
  track_key: string;
  email: string;
  name: string;
}): Promise<{
  enrollment: { kitchen_tenant_id: string };
  login_created: boolean;
  /** Only returned to a platform operator enrolling someone else; self-serve
   *  credentials go to the enrollee's mailbox. */
  password: string | null;
  credentials_sent?: boolean;
  note?: string;
}> {
  const { data } = await academyClient.post('/academy/enrollments', payload);
  return data;
}

export async function fetchMyEnrollment(trackKey?: string): Promise<MyEnrollment> {
  const { data } = await academyClient.get('/academy/enrollments/mine', {
    params: trackKey ? { track_key: trackKey } : undefined,
  });
  return data;
}

export async function addTrack(trackKey: string): Promise<void> {
  await academyClient.post('/academy/enrollments/add-track', { track_key: trackKey });
}

export async function fetchStats(): Promise<ProgramStats> {
  const { data } = await academyClient.get('/academy/stats');
  return data;
}

/** One row per enrollment (plus synthesized Whole PAI rows) — the same feed
 *  the roster connector consumes (/academy/export/learners). */
export interface LearnerRow {
  id: string;
  nombre: string;
  correo: string;
  certificacion: string;
  etapa: string;
  avance_pct: number;
  lecciones: string;
  receta: string;
  certificado: string;
  codigo_verificacion: string;
  badge: string;
  kitchen: string;
  inscrito_el: string | null;
  /** Failed the last exam and still inside the cooloff — a superadmin can lift
   *  it with unlockRetry so the learner retries now. */
  reintento_bloqueado: boolean;
}

export async function fetchLearners(): Promise<LearnerRow[]> {
  const { data } = await academyClient.get('/academy/export/learners');
  return Array.isArray(data) ? data : [];
}

/** Superadmin-only: clear a learner's exam cooloff so they can retry now. */
export async function unlockRetry(enrollmentId: string): Promise<void> {
  await academyClient.post(`/academy/enrollments/${enrollmentId}/unlock-retry`);
}

export async function fetchBadgeQuiz(): Promise<{
  title: string;
  pass_count: number;
  /** `course`: the hands-on course a question is about (QUIZ-3). */
  questions: { q: string; options: string[]; course?: string }[];
}> {
  const { data } = await academyClient.get('/academy/badge/quiz');
  return data;
}

export async function submitBadgeQuiz(payload: {
  email: string;
  name: string;
  answers: number[];
}): Promise<BadgeSubmitResult> {
  const { data } = await academyClient.post('/academy/badge/submit', payload);
  return data;
}

export async function proofStep(
  enrollmentId: string,
  stepId: string,
): Promise<{ passed: boolean; checks: CheckFeedback[] }> {
  const { data } = await academyClient.post(
    `/academy/enrollments/${enrollmentId}/steps/${stepId}/proof`,
  );
  return data;
}

export async function completeLesson(
  enrollmentId: string,
  lessonId: string,
  answers: number[],
): Promise<{ passed: boolean; wrong_questions: number[] }> {
  const { data } = await academyClient.post(
    `/academy/enrollments/${enrollmentId}/lessons/${lessonId}/complete`,
    { answers },
  );
  return data;
}

export async function startExam(enrollmentId: string): Promise<ExamAttempt> {
  const { data } = await academyClient.post(`/academy/enrollments/${enrollmentId}/exam`);
  return data;
}

export async function fetchExamStatus(enrollmentId: string): Promise<ExamAttempt> {
  const { data } = await academyClient.get(`/academy/enrollments/${enrollmentId}/exam`);
  return data;
}

export async function submitExam(
  enrollmentId: string,
  answers: Record<string, string>,
): Promise<{
  score: number;
  passed: boolean;
  pass_score: number;
  checks: { label: string; weight: number; passed: boolean }[];
  certificate: { id: string; verify_code: string; title: string } | null;
}> {
  const { data } = await academyClient.post(
    `/academy/enrollments/${enrollmentId}/exam/submit`,
    { answers },
  );
  return data;
}

// ── Programs (AWARE-1, AWARE-2) ─────────────────────────────────────────────
// A program groups tracks, the Easy as PAI badge and program lessons in order,
// and ends in a credential. maic PAI Aware closes with a multiple-choice
// certification exam drawn at random from a bank that never leaves the server.

export interface ProgramSummary {
  key: string;
  title: string;
  description: string;
  hours: number;
  items: number;
  has_exam: boolean;
  credential: string;
}

export interface ProgramExamRules {
  question_count: number;
  duration_minutes: number;
  pass_pct: number;
  max_attempts: number;
  cooloff_hours: number;
  requires_items: boolean;
  bank_size: number;
  courses: { key: string; title: string }[];
}

export interface ProgramDetail {
  key: string;
  version: string;
  title: string;
  description: string;
  hours: number;
  credential: string;
  sections: { title: string; items: { kind: string; key: string; title: string; course: string }[] }[];
  lessons: AcademyLesson[];
  exam: ProgramExamRules | null;
}

export type ProgramItemStatus = 'completed' | 'in_progress' | 'not_started';

export interface ProgramItem {
  kind: 'track' | 'badge' | 'lesson';
  key: string;
  section: string;
  course: string;
  title: string;
  description: string;
  status: ProgramItemStatus;
  progress_pct: number;
  enrolled: boolean;
}

export interface ProgramCertificate {
  id: string;
  verify_code: string;
  title: string;
  kind: string;
  holder: string;
  issued_at: string;
  /** The learner's own score; never on the PDF or the public verify page. */
  score: number | null;
  pdf_path: string;
  linkedin_url: string;
}

export interface ProgramExamState extends ProgramExamRules {
  attempts_used: number;
  attempts_left: number;
  passed: boolean;
  best_score: number | null;
  open_attempt: { id: string; attempt_no: number; deadline_at: string } | null;
  last_attempt: {
    id: string; attempt_no: number; score: number | null; passed: boolean;
    expired: boolean; submitted_at: string;
  } | null;
  next_attempt_at: string | null;
  can_start: boolean;
  blocked_reason: 'passed' | 'items' | 'no_attempts' | 'cooloff' | null;
}

export interface ProgramProgress {
  key: string;
  title: string;
  description: string;
  hours: number;
  credential: string;
  items_done: number;
  items_total: number;
  progress_pct: number;
  items?: ProgramItem[];
  exam: ProgramExamState | null;
  certificate: ProgramCertificate | null;
}

export interface ProgramAttempt {
  id: string;
  attempt_no: number;
  started_at: string;
  deadline_at: string;
  duration_minutes: number;
  pass_pct: number;
  questions: { id: string; q: string; options: string[] }[];
}

/** One question of the results page. On a failed attempt `correct` and
 *  `explanation` are null for the questions you missed. */
export interface ProgramReviewItem {
  id: string;
  q: string;
  course: string;
  options: string[];
  chosen: number | null;
  is_correct: boolean;
  correct: number | null;
  explanation: string | null;
}

export interface ProgramExamResult {
  id: string;
  attempt_no: number;
  submitted_at: string;
  passed: boolean;
  expired: boolean;
  score: number | null;
  correct: number;
  total: number;
  pass_pct: number;
  attempts_left: number;
  next_attempt_at: string | null;
  by_course: { course: string; title: string; correct: number; total: number }[];
  review: ProgramReviewItem[];
  certificate: ProgramCertificate | null;
  /** null: no certificate issued by this submit; otherwise the email outcome. */
  email_sent: boolean | null;
}

export async function listPrograms(): Promise<ProgramSummary[]> {
  const { data } = await academyClient.get('/academy/programs');
  return data.programs;
}

export async function fetchMyPrograms(): Promise<ProgramProgress[]> {
  const { data } = await academyClient.get('/academy/programs/mine');
  return data.programs;
}

export async function fetchProgram(key: string): Promise<ProgramDetail> {
  const { data } = await academyClient.get(`/academy/programs/${key}`);
  return data;
}

export async function fetchProgramProgress(key: string): Promise<ProgramProgress> {
  const { data } = await academyClient.get(`/academy/programs/${key}/progress`);
  return data;
}

export async function completeProgramLesson(
  key: string, lessonId: string, answers: number[],
): Promise<{ passed: boolean; wrong_questions: number[]; status: string }> {
  const { data } = await academyClient.post(
    `/academy/programs/${key}/lessons/${lessonId}/complete`, { answers },
  );
  return data;
}

export async function startProgramExam(key: string): Promise<ProgramAttempt> {
  const { data } = await academyClient.post(`/academy/programs/${key}/exam`);
  return data;
}

export async function submitProgramExam(
  key: string, attemptId: string, answers: Record<string, number | null>,
): Promise<ProgramExamResult> {
  const { data } = await academyClient.post(
    `/academy/programs/${key}/exam/${attemptId}/submit`, { answers },
  );
  return data;
}

export async function fetchProgramAttempt(
  key: string, attemptId: string,
): Promise<{ status: 'open'; attempt: ProgramAttempt } | { status: 'submitted'; results: ProgramExamResult }> {
  const { data } = await academyClient.get(`/academy/programs/${key}/exam/${attemptId}`);
  return data;
}

export default academyClient;

// ── Opinion form per course and exam (QUIZ-4, INTRO-5) ──────────────────────

export type FeedbackKind = 'track' | 'track_exam' | 'program' | 'program_exam' | 'badge';

export interface MyFeedback {
  kind: FeedbackKind;
  subject_key: string;
  rating: number;
  comment: string;
  updated_at: string | null;
}

export interface FeedbackSubjectSummary {
  kind: FeedbackKind;
  kind_label: string;
  subject_key: string;
  title: string;
  count: number;
  avg_rating: number;
  /** How many opinions gave 1, 2, 3, 4 and 5 stars. */
  distribution: number[];
  comments: number;
  recent_comments: { name: string; email: string; rating: number; comment: string; updated_at: string | null }[];
}

export interface FeedbackSummary {
  total: number;
  learners: number;
  avg_rating: number | null;
  with_comment: number;
  subjects: FeedbackSubjectSummary[];
}

export async function fetchMyFeedback(): Promise<MyFeedback[]> {
  const { data } = await academyClient.get('/academy/feedback/mine');
  return data.feedback ?? [];
}

export async function saveFeedback(payload: {
  kind: FeedbackKind; subject_key: string; rating: number; comment: string;
}): Promise<MyFeedback> {
  const { data } = await academyClient.put('/academy/feedback', payload);
  return data;
}

export async function fetchFeedbackSummary(): Promise<FeedbackSummary> {
  const { data } = await academyClient.get('/academy/feedback/summary');
  return data;
}
